import { environment } from '@env';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, first, switchMap, tap } from 'rxjs/operators';
import { AuthRemote } from './auth.remote';
import { Store } from '@ngrx/store';
import { selectCurrentLocale } from '@core/modules/language/features/language.slice';
import { logout } from '@core/modules/user/features/user.slice';
import { Router } from '@angular/router';
import { LOGIN } from '@core/constants/RoutingsPath';
import { BasicInformation } from '@core/modules/user/models';
import { RegisterUser } from '@core/models/register-user';
import { RegisterUserApiService } from './register-user-api.service';
import { PasswordsApiService } from './passwords-api.service';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'any'
})
export class AuthService {
  API_URL = environment.api;
  AUTH_API = `${environment.api}auth/`;

  httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
  };

  constructor(
    private readonly http: HttpClient,
    private readonly authRemote: AuthRemote,
    private readonly messageService: MessageService,
    private readonly translate: TranslateService,
    private readonly registerUserApiService: RegisterUserApiService,
    private readonly passwordsApiService: PasswordsApiService,
    private readonly store: Store,
    private readonly router: Router
  ) {}

  updateTokenLanguage(data: Partial<BasicInformation>): Observable<string> {
    return this.authRemote.updateTokenLanguage(data);
  }

  login(email: string, password: string): Observable<any> {
    return this.http.post(
      this.API_URL + 'login',
      {
        email,
        password
      },
      this.httpOptions
    );
  }

  logout(): void {
    this.store.dispatch(logout());
    this.router.navigate([LOGIN]);
  }

  register(registerUser: RegisterUser): Observable<any> {
    return this.store.select(selectCurrentLocale).pipe(
      first(),
      switchMap((currentLocale) =>
        this.registerUserApiService.registerUser({
          ...registerUser,
          lang: currentLocale?.id
        })
      )
    );
  }

  resendActivationLink(email: string): Observable<any> {
    return this.authRemote.resendActivationLink(email);
  }

  refreshToken(token: string) {
    return this.http.post(
      this.API_URL + 'refreshtoken',
      {
        refreshToken: token
      },
      this.httpOptions
    );
  }

  remember(email: string) {
    return this.passwordsApiService.sendResetUserPassword(email).pipe(
      tap(() => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('remember.ok-title'),
          detail: this.translate.instant('remember.ok', { email }),
          sticky: true
        });
      }),
      catchError(() => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('remember.error-title'),
          detail: this.translate.instant('remember.error')
        });

        return throwError(() => new Error('Error remember'));
      })
    );
  }

  getInfoUser(email: string): Observable<any> {
    return this.http.get<any>(this.API_URL + 'users/' + email);
  }

  activeUser(token: string) {
    return this.http.get<any>(this.API_URL + 'activate/' + token);
  }

  resetPassword(token: string, email: string, password: string, password_confirmation: string) {
    return this.http.put(
      this.API_URL + 'reset-password',
      {
        token,
        email,
        password,
        password_confirmation
      },
      this.httpOptions
    );
  }

  changePassword(
    currentPassword: string,
    newPassword: string,
    confirmPassword: string,
    userId: number
  ): Observable<void> {
    return this.http.put<void>(this.API_URL + 'change-password', {
      currentPassword,
      newPassword,
      confirmPassword,
      id: userId.toString()
    });
  }
}
